import React, { useEffect } from "react";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useFeedPost } from "./hooks/useFeedPost";
import { useBreadcrumb } from "@/components/ui/Breadcrumb";
import { RemoveButton } from "@/components/RemoveButton";
import { useDestroyFeedPost } from "@/hooks/useDestroyFeedPost";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { Loader } from "@/components/Loader";

export const FeedPostDetails = () => {
  const { t } = useTranslation();
  const { feedPostId } = useParams<{ feedPostId: string }>();
  const { feedPost, loading } = useFeedPost(feedPostId);
  const { navToRoute } = useProtectedNavigation();
  const { setDynamicTitle } = useBreadcrumb();

  const handleDeleteSuccess = () => {
    navToRoute("feedPosts");
  };

  const { destroyFeedPost, loading: deleteLoading } = useDestroyFeedPost({
    onSuccess: handleDeleteSuccess,
  });

  const handleDelete = () => {
    if (feedPostId) {
      destroyFeedPost(feedPostId);
    }
  };

  useEffect(() => {
    if (feedPost?.name) {
      setDynamicTitle(feedPost.name);
    }
  }, [feedPost, setDynamicTitle]);

  useEffect(() => {
    return () => {
      setDynamicTitle(null);
    };
  }, [setDynamicTitle]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!feedPost) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-foreground">
          {t("feedPostDetails.details.notFound")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mb-4 flex w-full items-center pt-6 pr-6 pl-8">
        <h1 className="grow text-2xl font-semibold text-foreground">
          {feedPost.name || t("feedPostDetails.title")}
        </h1>
        <RemoveButton
          alertTitle={t("feedPostDetails.details.deleteConfirm.title")}
          alertDescription={t("feedPostDetails.details.deleteConfirm.message")}
          deleteAction={handleDelete}
          disabled={deleteLoading}
          loading={deleteLoading}
          size="sm"
          variant="outline"
        >
          {t("feedPostDetails.details.delete")}
        </RemoveButton>
      </div>

      <div className="ml-8">
        <div className="flex gap-10">
          <TabLink to="" text={t("feedPostDetails.details.tabs.postDetails")} />
          {/* <TabLink
            to="results"
            text={t("feedPostDetails.details.tabs.results")}
          /> */}
        </div>
      </div>

      <div className="w-full border-b border-border"></div>

      <div className="px-8 pt-8 pb-10">
        <Outlet context={{ feedPost, loading }} />
      </div>
    </div>
  );
};

const TabLink = ({ to, text }: { to: string; text: string }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      cn(
        "pb-4 text-sm transition-colors",
        isActive
          ? "border-b-2 border-primary font-semibold text-foreground"
          : "text-muted-foreground hover:text-foreground",
      )
    }
  >
    {text}
  </NavLink>
);
